import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useOutletContext } from 'react-router-dom';
import { 
  MessageSquare, 
  Heart, 
  Share2, 
  Image as ImageIcon, 
  Video, 
  Send, 
  Flame, 
  Sparkles, 
  Hash, 
  CheckCircle2, 
  TrendingUp, 
  Sliders, 
  Layers,
  Award
} from 'lucide-react';
import { fetchCommunityPosts, createCommunityPost, togglePostLike, fetchPostComments, addPostComment } from '../api/client';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button';
import './Community.css';

// ── Single Post Thread Component ──
function PostItem({ post, onLikeToggle, onOpenAuth }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [copied, setCopied] = useState(false);

  // Fetch comments query
  const { data: commentsData, isLoading: commentsLoading } = useQuery({
    queryKey: ['post-comments', post.id],
    queryFn: () => fetchPostComments(post.id),
    enabled: showComments,
  });

  const comments = commentsData?.data || [];

  // Add comment mutation
  const commentMutation = useMutation({
    mutationFn: (text) => addPostComment(post.id, text),
    onSuccess: () => {
      setNewComment('');
      queryClient.invalidateQueries(['post-comments', post.id]);
      queryClient.invalidateQueries(['community-posts']);
    },
  });

  const handleSendComment = (e) => {
    e.preventDefault();
    if (!user) {
      if (onOpenAuth) onOpenAuth();
      return;
    }
    if (!newComment.trim()) return;
    commentMutation.mutate(newComment.trim());
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <article className="community-post-card glass-panel animate-fade-in">
      <div className="post-header-row">
        <img 
          src={post.author_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${post.user_id}`} 
          alt={post.author_name} 
          className="post-author-avatar" 
        />
        <div className="post-header-meta">
          <div className="post-author-name-row">
            <span className="post-author-name">{post.author_name}</span>
            {post.author_role === 'designer' && (
              <span className="pro-designer-badge">PRO</span>
            )}
            <span className="post-timestamp">
              {new Date(post.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </span>
          </div>
          <span className="post-author-spec">{post.author_specialization || 'Graphic Designer'}</span>
        </div>
      </div>

      <div className="post-body-text">
        <p>{post.content}</p>
      </div>

      {post.tags && post.tags.length > 0 && (
        <div className="post-tags-row">
          {post.tags.map((tag, tIdx) => (
            <span key={tIdx} className="post-tag-chip">
              {tag.startsWith('#') ? tag : `#${tag}`}
            </span>
          ))}
        </div>
      )}

      {post.media_url && (
        <div className="post-media-container">
          {post.media_type === 'video' ? (
            <video 
              src={post.media_url} 
              controls 
              muted 
              loop 
              playsInline 
              className="post-video-player"
            />
          ) : (
            <img 
              src={post.media_url} 
              alt="Post attachment" 
              className="post-image-attachment" 
              loading="lazy"
            />
          )}
        </div>
      )}

      <div className="post-footer-actions">
        <button 
          className={`post-action-btn like-btn ${post.has_liked ? 'liked' : ''}`}
          onClick={() => onLikeToggle(post.id)}
        >
          <Heart size={18} fill={post.has_liked ? 'currentColor' : 'none'} />
          <span>{post.likes_count || 0}</span>
        </button>

        <button 
          className={`post-action-btn comment-btn ${showComments ? 'active' : ''}`}
          onClick={() => setShowComments(!showComments)}
        >
          <MessageSquare size={18} />
          <span>{post.comments_count || 0}</span>
        </button>

        <button className="post-action-btn share-btn" onClick={handleShare}>
          <Share2 size={18} />
          <span>{copied ? 'Copied' : 'Share'}</span>
        </button>
      </div>

      {/* ── Thread Comments Section ── */}
      {showComments && (
        <div className="post-comments-thread">
          <form onSubmit={handleSendComment} className="comment-composer-row">
            <input 
              type="text" 
              className="comment-input" 
              placeholder="Drop your critique or feedback..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
            <button type="submit" className="comment-send-btn" disabled={commentMutation.isPending}>
              <Send size={15} />
            </button>
          </form>

          {commentsLoading ? (
            <div className="comments-loading">Loading replies...</div>
          ) : comments.length === 0 ? (
            <div className="comments-empty">No critiques yet. Share the first review!</div>
          ) : (
            <div className="comments-list">
              {comments.map((comment) => (
                <div key={comment.id} className="comment-item">
                  <img src={comment.author_avatar} alt={comment.author_name} className="comment-avatar" />
                  <div className="comment-bubble">
                    <div className="comment-meta">
                      <span className="comment-author">{comment.author_name}</span>
                      <span className="comment-time">
                        {new Date(comment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="comment-content">{comment.content}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function Community() {
  const { user } = useAuth();
  const { setAuthOpen } = useOutletContext() || {};
  const queryClient = useQueryClient();

  const [selectedTag, setSelectedTag] = useState('all');
  const [postText, setPostText] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaType, setMediaType] = useState('image');
  const [selectedPostTags, setSelectedPostTags] = useState(['critique']);
  const [isMediaInputOpen, setIsMediaInputOpen] = useState(false);

  const availableTags = [
    { id: 'all', label: 'All Creative Pulses' },
    { id: 'critique', label: '#critique' },
    { id: 'wip', label: '#wip' },
    { id: 'motion', label: '#motiongraphics' },
    { id: 'flyerdesign', label: '#flyerdesign' },
    { id: 'uiux', label: '#uiux' },
    { id: 'designtips', label: '#designtips' },
    { id: 'branding', label: '#branding' },
  ];

  const composerTags = ['critique', 'wip', 'motion', 'flyerdesign', 'uiux', 'typography', 'branding'];

  const { data, isLoading } = useQuery({
    queryKey: ['community-posts', selectedTag],
    queryFn: () => fetchCommunityPosts({
      tag: selectedTag !== 'all' ? selectedTag : undefined
    }),
  });

  const posts = data?.data || [];

  // Create post mutation
  const createPostMutation = useMutation({
    mutationFn: (payload) => createCommunityPost(payload),
    onSuccess: () => {
      setPostText('');
      setMediaUrl('');
      setIsMediaInputOpen(false);
      queryClient.invalidateQueries(['community-posts']);
    },
  });

  // Like mutation
  const likeMutation = useMutation({
    mutationFn: (postId) => togglePostLike(postId),
    onSuccess: () => {
      queryClient.invalidateQueries(['community-posts']);
    },
  });

  const handleCreatePost = (e) => {
    e.preventDefault();
    if (!user) {
      if (setAuthOpen) setAuthOpen(true);
      return;
    }
    if (!postText.trim()) return;

    createPostMutation.mutate({
      content: postText.trim(),
      media_url: mediaUrl.trim() || undefined,
      media_type: mediaUrl.trim() ? mediaType : undefined,
      tags: selectedPostTags.map(t => `#${t}`),
    });
  };

  const handleLikeToggle = (postId) => {
    if (!user) {
      if (setAuthOpen) setAuthOpen(true);
      return;
    }
    likeMutation.mutate(postId);
  };

  const toggleComposerTag = (tag) => {
    setSelectedPostTags(prev => 
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  return (
    <div className="community-page page-container">
      <div className="community-layout-grid">
        {/* ── Main Feed Column ── */}
        <main className="community-feed-col">
          {/* Post Composer Card */}
          <div className="composer-card glass-panel">
            <form onSubmit={handleCreatePost}>
              <div className="composer-top-row">
                <img 
                  src={user ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.id}` : 'https://api.dicebear.com/7.x/avataaars/svg?seed=guest'} 
                  alt="Avatar" 
                  className="composer-avatar" 
                />
                <textarea 
                  className="composer-textarea"
                  rows={3}
                  placeholder={user ? "What are you designing right now? Share a WIP, ask for critiques, or share a pro tip..." : "Log in to join the designer conversation..."}
                  value={postText}
                  onChange={(e) => setPostText(e.target.value)}
                  disabled={!user}
                />
              </div>

              {isMediaInputOpen && (
                <div className="composer-media-inputs animate-fade-in">
                  <div className="media-type-toggle">
                    <button 
                      type="button" 
                      className={`type-btn ${mediaType === 'image' ? 'active' : ''}`}
                      onClick={() => setMediaType('image')}
                    >
                      <ImageIcon size={14} /> Image
                    </button>
                    <button 
                      type="button" 
                      className={`type-btn ${mediaType === 'video' ? 'active' : ''}`}
                      onClick={() => setMediaType('video')}
                    >
                      <Video size={14} /> Motion Video (MP4)
                    </button>
                  </div>
                  <input 
                    type="url" 
                    className="media-url-input" 
                    placeholder={mediaType === 'video' ? 'Enter direct MP4 video URL...' : 'Enter high-res preview image URL...'}
                    value={mediaUrl}
                    onChange={(e) => setMediaUrl(e.target.value)}
                  />
                </div>
              )}

              <div className="composer-tag-selector">
                <span className="tag-picker-label">Tags:</span>
                {composerTags.map(tag => (
                  <button
                    type="button"
                    key={tag}
                    className={`composer-tag-chip ${selectedPostTags.includes(tag) ? 'selected' : ''}`}
                    onClick={() => toggleComposerTag(tag)}
                  >
                    #{tag}
                  </button>
                ))}
              </div>

              <div className="composer-footer-row">
                <div className="composer-tools">
                  <button 
                    type="button" 
                    className={`tool-icon-btn ${isMediaInputOpen ? 'active' : ''}`}
                    onClick={() => setIsMediaInputOpen(!isMediaInputOpen)}
                    title="Attach Image or Video Link"
                  >
                    <ImageIcon size={18} />
                  </button>
                </div>

                <Button 
                  type="submit" 
                  variant="primary" 
                  disabled={!user || !postText.trim() || createPostMutation.isPending}
                >
                  <Send size={15} />
                  <span>{createPostMutation.isPending ? 'Publishing...' : 'Publish Pulse'}</span>
                </Button>
              </div>
            </form>
          </div>

          {/* Tag Filter Pills */}
          <div className="community-tags-bar">
            {availableTags.map(tag => (
              <button
                key={tag.id}
                className={`community-tag-pill ${selectedTag === tag.id ? 'active' : ''}`}
                onClick={() => setSelectedTag(tag.id)}
              >
                {tag.label}
              </button>
            ))}
          </div>

          {/* Posts List */}
          {isLoading ? (
            <div className="feed-loading">
              <div className="spinner" />
              <p>Gathering designer pulse...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="feed-empty glass-panel">
              <MessageSquare size={44} className="text-secondary" />
              <h3>No pulses found for this tag</h3>
              <p>Be the first creative to start the discussion!</p>
            </div>
          ) : (
            <div className="community-posts-list">
              {posts.map(post => (
                <PostItem 
                  key={post.id} 
                  post={post} 
                  onLikeToggle={handleLikeToggle}
                  onOpenAuth={() => setAuthOpen && setAuthOpen(true)}
                />
              ))}
            </div>
          )}
        </main>

        {/* ── Sidebar Right Column ── */}
        <aside className="community-sidebar-col">
          {/* Trending Topics Card */}
          <div className="sidebar-widget glass-panel">
            <div className="widget-header">
              <TrendingUp size={18} className="text-primary" />
              <h4>Trending Channels</h4>
            </div>
            <div className="trending-channels-list">
              <button className="channel-item" onClick={() => setSelectedTag('critique')}>
                <div className="channel-name">#critique</div>
                <span className="channel-count">Design reviews & constructive feedback</span>
              </button>
              <button className="channel-item" onClick={() => setSelectedTag('motion')}>
                <div className="channel-name">#motiongraphics</div>
                <span className="channel-count">After Effects, Blender & 3D kinetic loops</span>
              </button>
              <button className="channel-item" onClick={() => setSelectedTag('flyerdesign')}>
                <div className="channel-name">#flyerdesign</div>
                <span className="channel-count">Afrobeat flyers, concert & church posters</span>
              </button>
              <button className="channel-item" onClick={() => setSelectedTag('uiux')}>
                <div className="channel-name">#uiux</div>
                <span className="channel-count">Figma systems, micro-interactions & apps</span>
              </button>
            </div>
          </div>

          {/* Community Guidelines Card */}
          <div className="sidebar-widget glass-panel">
            <div className="widget-header">
              <Award size={18} className="text-yellow-400" />
              <h4>Creative Code</h4>
            </div>
            <ul className="creative-code-list">
              <li>
                <CheckCircle2 size={15} className="text-green-400" />
                <span>Give constructive, actionable design critiques.</span>
              </li>
              <li>
                <CheckCircle2 size={15} className="text-green-400" />
                <span>Respect original intellectual property and licenses.</span>
              </li>
              <li>
                <CheckCircle2 size={15} className="text-green-400" />
                <span>Share source tips, typography scales, and palette codes.</span>
              </li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
